/**
 * Self-contained CSS for the morph-bar AgentSearch surface. Verbatim port
 * of the user's reference component (`/tmp/agent-search-2/AgentSearch.css`)
 * with the theme block re-pointed at our @ordo/ui Tailwind tokens so the
 * morph picks up the host app's light/dark mode automatically.
 *
 * Hard constraint per the reference component's README: this lives outside
 * Tailwind because the morph relies on a single DOM node animating
 * width/height/border-radius — porting the keyframes + stagger to Tailwind
 * loses the polish for no gain.
 *
 * Injected into `document.head` once at module load (deduped via id).
 */

export const AGENT_SEARCH_STYLE_ID = 'ordo-agent-search-styles'

export const agentSearchCss = `
/* Theme + animation tokens. Hardcoded light defaults on :root with
   dark overrides on .dark — the morph self-themes regardless of how
   each host configures CSS variables. Two prior approaches failed:
     (a) hsl(var(--background)) — broke on hosts where --background
         is itself an hsl() call (HQ via @ordo/ui/theme), giving
         invalid hsl(hsl(...)).
     (b) var(--color-background, fallback) — Tailwind v4's @theme
         inline block doesn't actually emit --color-* as CSS custom
         properties (it only registers them as utility-class targets),
         so the fallback always won and dark mode broke everywhere.
   Hosts that want to override the embed palette can still set
   --as-* directly in their own CSS. */
:root {
  --as-bg-elevated: hsl(0 0% 100%);
  --as-border: hsl(0 0% 89.8%);
  --as-border-strong: hsl(0 0% 80%);
  --as-text: hsl(0 0% 3.9%);
  --as-text-muted: hsl(0 0% 45.1%);
  --as-text-subtle: hsl(0 0% 56%);
  --as-hover: hsl(0 0% 96.1%);
  --as-kbd-bg: hsl(0 0% 96.1%);
  --as-kbd-border: hsl(0 0% 89.8%);
  --as-accent: hsl(230 78% 56%);

  --as-shadow-bar:
    0 1px 2px rgba(15, 15, 20, 0.03);
  --as-shadow-bar-hover:
    0 1px 3px rgba(15, 15, 20, 0.05);
  --as-shadow-modal:
    0 0 0 1px rgba(15, 15, 20, 0.05),
    0 24px 64px -16px rgba(15, 15, 20, 0.20),
    0 8px 24px -8px rgba(15, 15, 20, 0.08);

  --as-ease-spring: cubic-bezier(0.32, 0.72, 0, 1);
  --as-ease-grow: cubic-bezier(0.2, 0.9, 0.25, 1);
  --as-ease-out: cubic-bezier(0.16, 1, 0.3, 1);

  --as-closed-w: 460px;
  --as-open-w: 640px;
  --as-open-h: 540px;
  /* Viewport-y anchor for the morph. Sized to align with a typical
     size="sm" AppTopBar (~52px tall) — pill sits centered in the bar.
     Apps with a different topbar height can override on :root or on
     the placeholder via inline style. */
  --as-anchor-top: 6px;
}

/* Dark overrides. Hosts apply .dark on <html> (Tasker / HQ / etc.); the
   portaled morph lives under <body> so it inherits this rule's vars.
   Specificity matters: :root above is (0,1,0). :where(.dark) is (0,0,0)
   — strips to zero — and loses the cascade. :root.dark is (0,2,0) and
   wins outright; .dark is the extra coverage if a host ever applies the
   class to a non-root element. */
:root.dark, .dark {
  --as-bg-elevated: hsl(225 5% 17%);
  --as-border: hsl(225 4% 23%);
  --as-border-strong: hsl(225 4% 30%);
  --as-text: hsl(0 0% 98%);
  --as-text-muted: hsl(220 5% 60%);
  --as-text-subtle: hsl(220 5% 42%);
  --as-hover: hsl(225 5% 22%);
  --as-kbd-bg: hsl(225 5% 20%);
  --as-kbd-border: hsl(225 4% 23%);
  --as-accent: hsl(217 91% 60%);
}

/* Inline pill — rendered in the host's React tree at the AgentTopbar
   slot. Participates in normal flex layout + stacking, so full-screen
   host surfaces (task detail panels, modals, sheets) cover it the way
   they cover any other topbar element. No z-index gymnastics needed. */
.as-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 14px;
  width: 100%;
  max-width: var(--as-closed-w);
  height: 44px;
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  border-radius: 12px;
  box-shadow: var(--as-shadow-bar);
  cursor: text;
  font-family: inherit;
  font-size: inherit;
  color: var(--as-text);
  text-align: left;
  transition: border-color 200ms, box-shadow 200ms;
}
/* Compact closed state (PROD-1403): an icon-only magnifying glass that
   matches the host's notification bell button exactly (@ordo/ui
   NotificationCenter trigger: h-9 w-9 rounded-md, muted icon, muted hover
   wash, no border) and sits right next to it. Wins over the mobile rule
   that stretches the pill. */
.as-pill.as-pill-compact {
  width: 36px;
  max-width: none;
  flex: 0 0 auto;
  height: 36px;
  gap: 0;
  padding: 0;
  justify-content: center;
  border: 0;
  border-radius: 6px;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  /* --as-* tokens, not the host's --muted/--foreground: most apps define
     those as bare HSL triplets, which made this pill render brighter than
     the bell beside it. */
  color: var(--as-text-muted);
  transition: background-color 150ms, color 150ms;
}
.as-pill.as-pill-compact:hover {
  background: var(--as-hover);
  color: var(--as-text);
  border-color: transparent;
  box-shadow: none;
}
.as-pill.as-pill-compact:focus-visible {
  box-shadow: 0 0 0 2px var(--as-border-strong);
}
.as-pill:hover {
  border-color: var(--as-border-strong);
  box-shadow: var(--as-shadow-bar-hover);
}
.as-pill:focus-visible {
  outline: none;
  border-color: var(--as-border-strong);
  box-shadow: 0 0 0 2px var(--as-border-strong);
}
/* While the morph is mounted (opening / open / closing) the inline
   pill stays in flow but invisible — no layout shift, no double click
   target. visibility:hidden keeps the box; pointer-events:none is
   implied by visibility but spelt out for clarity. */
.as-pill-hidden {
  visibility: hidden;
  pointer-events: none;
}

.as-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 20, 0.04);
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
  opacity: 0;
  pointer-events: none;
  z-index: 2147483646;
  transition:
    opacity 280ms var(--as-ease-out),
    backdrop-filter 280ms var(--as-ease-out);
}
.as-backdrop-show {
  opacity: 1;
  pointer-events: auto;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  background: rgba(15, 15, 20, 0.16);
}
/* Backdrop while the morph is idle — fully invisible, no transition
   firing. (Default values would already be invisible, but suppressing
   transitions keeps the unmount frame clean.) */
.as-backdrop-idle {
  transition: none;
  opacity: 0;
  backdrop-filter: blur(0px);
  -webkit-backdrop-filter: blur(0px);
}

/* The morph element — portaled to document.body, always mounted so
   the input is in DOM for synchronous focus (iOS Safari). Closed:
   hidden via .as-search-idle. Opening / closing: top/left/width/
   height inline-styled to the pill's rect (FLIP). Open: .as-search-open
   class snaps to centered viewport-modal dimensions. transform stays
   translateX(-50%) constant — only the four spatial properties animate. */
.as-search {
  position: fixed;
  top: var(--as-anchor-top);
  left: 50%;
  transform: translateX(-50%);
  width: var(--as-closed-w);
  height: 44px;
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  border-radius: 12px;
  box-shadow: var(--as-shadow-bar);
  overflow: hidden;
  cursor: default;
  font-family: inherit;
  color: var(--as-text);
  z-index: 2147483647;
  transition:
    width 520ms var(--as-ease-spring),
    height 520ms var(--as-ease-spring),
    top 520ms var(--as-ease-spring),
    left 520ms var(--as-ease-spring),
    border-radius 520ms var(--as-ease-spring),
    box-shadow 320ms var(--as-ease-out),
    border-color 200ms;
  will-change: width, height, top, left, border-radius;
}

/* Idle state — morph is mounted but invisible (closed). Transitions
   suppressed so the un-style on stage='closed' doesn't fire a hidden
   animation in the background. */
.as-search-idle {
  visibility: hidden;
  pointer-events: none;
  transition: none;
}

/* Instant state — first paint of the opening transition, where the
   inline-styled rect is committed without animating. The next frame
   adds .as-search-open and re-arms transitions, so the morph springs
   from the pill's rect → centered. */
.as-search-instant {
  transition: none !important;
}

.as-search-open {
  /* Snap to viewport-centered open dimensions. !important because
     the opening / closing states write inline top/left/width/height;
     once we add this class, those inline values must lose to the
     centered-modal target. */
  top: var(--as-anchor-top) !important;
  left: 50% !important;
  width: var(--as-open-w) !important;
  height: var(--as-open-h) !important;
  border-radius: 16px !important;
  box-shadow: var(--as-shadow-modal);
  cursor: default;
}

/* Chat-mode dimensions: more room for messages once a conversation
   is going. Same spring transition (already on .as-search) animates
   the dimension swap when entering/leaving chat. Desktop only —
   gating with min-width:641px keeps the rule from leaking into the
   mobile media query, where the .as-search-open !important rules
   should win for both empty palette and chat. */
@media (min-width: 641px) {
  /* !important on width/height matches the same on .as-search-open
     above — without it the higher-specificity chat-mode selector
     still loses the !important race to the base open rule. */
  .as-search-open.as-chat-mode {
    width: 800px !important;
    height: min(80vh, 720px) !important;
    max-width: calc(100vw - 32px);
    max-height: calc(100vh - 32px);
  }
}

.as-closed {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  padding: 0 14px;
  gap: 10px;
  opacity: 1;
  transition: opacity 140ms var(--as-ease-out);
}
.as-search-open .as-closed {
  opacity: 0;
  transition: opacity 100ms;
  pointer-events: none;
}

.as-placeholder {
  flex: 1;
  color: var(--as-text-subtle);
  font-size: 14px;
}

.as-sparkle {
  display: inline-block;
  flex-shrink: 0;
  position: relative;
}
.as-sparkle svg {
  width: 100%;
  height: 100%;
  display: block;
}
.as-sparkle-stop-1 { animation: as-hue-1 6s linear infinite; }
.as-sparkle-stop-2 { animation: as-hue-2 6s linear infinite; }

@keyframes as-hue-1 {
  0%   { stop-color: #6366f1; }
  33%  { stop-color: #8b5cf6; }
  66%  { stop-color: #ec4899; }
  100% { stop-color: #6366f1; }
}
@keyframes as-hue-2 {
  0%   { stop-color: #ec4899; }
  33%  { stop-color: #6366f1; }
  66%  { stop-color: #8b5cf6; }
  100% { stop-color: #ec4899; }
}

.as-kbd {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 11px;
  color: var(--as-text-muted);
  background: var(--as-kbd-bg);
  border: 1px solid var(--as-kbd-border);
  border-bottom-width: 1.5px;
  border-radius: 5px;
  padding: 2px 6px;
  min-width: 22px;
  text-align: center;
  font-weight: 500;
  display: inline-block;
  margin: 0 1px;
}
.as-kbd-sm {
  font-size: 10px;
  padding: 1px 5px;
  min-width: 18px;
}

.as-open {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  opacity: 0;
  pointer-events: none;
  transition: opacity 180ms var(--as-ease-out);
}
.as-search-open .as-open {
  opacity: 1;
  pointer-events: auto;
  transition: opacity 220ms var(--as-ease-out) 180ms;
}

.as-input-row {
  display: flex;
  align-items: center;
  padding: 18px 20px;
  border-bottom: 1px solid var(--as-border);
  gap: 12px;
  flex-shrink: 0;
}
.as-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font: inherit;
  font-size: 17px;
  letter-spacing: -0.015em;
  color: var(--as-text);
  caret-color: var(--as-accent);
}
.as-input::placeholder { color: var(--as-text-subtle); }

.as-kbd-esc {
  font-size: 11px;
  padding: 3px 7px;
}

.as-body {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}
.as-body::-webkit-scrollbar { width: 0; }

.as-section-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--as-text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 12px 20px 6px;
}

.as-prompts {
  padding: 4px 14px 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.as-chip {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 11px;
  border: 1px solid var(--as-border);
  border-radius: 999px;
  font: inherit;
  font-size: 13px;
  color: var(--as-text);
  background: var(--as-bg-elevated);
  cursor: pointer;
  white-space: nowrap;
  transition: all 140ms var(--as-ease-out);
}
.as-chip:hover {
  background: var(--as-hover);
  border-color: var(--as-border-strong);
  transform: translateY(-1px);
}

.as-row {
  display: flex;
  align-items: center;
  padding: 9px 20px;
  gap: 12px;
  cursor: pointer;
  transition: background 100ms;
  position: relative;
}
.as-row:hover, .as-row-active { background: var(--as-hover); }
/* No left-edge accent bar — the background tint alone reads as
   "active". Adding a 2px indigo bar on top of that is redundant
   and visually noisy. */
/* Row icon: simple flat icon, no tinted tile. Matches the sidebar's
   bare icon + text pattern instead of the iOS app-grid feel that
   colored tiles gave it. Tint classes kept as a no-op so callers
   that still pass tint do not break, but they no longer render a
   colored background. */
.as-row-icon {
  width: 18px;
  height: 18px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  color: var(--as-text-muted);
  font-size: 12px;
}
.as-tint-blue,
.as-tint-green,
.as-tint-amber,
.as-tint-violet,
.as-tint-pink,
.as-tint-gray {
  background: transparent;
  color: var(--as-text-muted);
}

.as-row-title {
  flex: 1;
  font-size: 14px;
  color: var(--as-text);
  letter-spacing: -0.005em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.as-row-meta {
  font-size: 12px;
  color: var(--as-text-subtle);
  flex-shrink: 0;
}
.as-row-shortcut {
  display: flex;
  gap: 3px;
  flex-shrink: 0;
}

.as-row-completed .as-row-title {
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  color: var(--as-text-subtle);
}
.as-row-completed .as-row-icon,
.as-row-completed .as-row-meta {
  color: var(--as-text-subtle);
}

.as-empty {
  padding: 32px 20px;
  text-align: center;
  font-size: 13px;
  color: var(--as-text-subtle);
}

.as-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 6px;
}
.as-divider-line {
  flex: 1;
  height: 1px;
  background: var(--as-border);
}
.as-divider-label {
  font-size: 10.5px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--as-text-muted);
}

.as-failed-line {
  padding: 8px 20px 12px;
  font-size: 11.5px;
  color: var(--as-text-muted);
  font-style: italic;
}

/* Skeleton placeholders for in-flight live search. Mirrors .as-row
 * dimensions so results land into the same layout slot — no layout
 * shift between "searching" and "found". */
.as-skeleton-line {
  display: inline-block;
  height: 12px;
  border-radius: 6px;
  background: linear-gradient(90deg, var(--as-row-hover) 0%, var(--as-bg-elevated) 50%, var(--as-row-hover) 100%);
  background-size: 200% 100%;
  animation: as-skeleton-pulse 1.4s ease-in-out infinite;
}
.as-skeleton-line-md { width: 60%; }
.as-skeleton-dot {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 999px;
  background: var(--as-row-hover);
  animation: as-skeleton-pulse 1.4s ease-in-out infinite;
}
@keyframes as-skeleton-pulse {
  0%, 100% { opacity: 0.55; background-position: 0% 0; }
  50% { opacity: 1; background-position: 100% 0; }
}

.as-footer {
  border-top: 1px solid var(--as-border);
  padding: 9px 20px;
  display: flex;
  gap: 18px;
  font-size: 11.5px;
  color: var(--as-text-muted);
  flex-shrink: 0;
  background: var(--as-bg-elevated);
  filter: brightness(0.99);
}
.as-footer span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.as-search-open .as-input-row {
  animation: as-reveal 480ms var(--as-ease-spring) backwards 200ms;
}
.as-search-open .as-reveal {
  animation: as-reveal 480ms var(--as-ease-spring) backwards;
}
.as-search-open .as-reveal:nth-child(1)  { animation-delay: 240ms; }
.as-search-open .as-reveal:nth-child(2)  { animation-delay: 270ms; }
.as-search-open .as-reveal:nth-child(3)  { animation-delay: 300ms; }
.as-search-open .as-reveal:nth-child(4)  { animation-delay: 330ms; }
.as-search-open .as-reveal:nth-child(5)  { animation-delay: 360ms; }
.as-search-open .as-reveal:nth-child(6)  { animation-delay: 390ms; }
.as-search-open .as-reveal:nth-child(7)  { animation-delay: 420ms; }
.as-search-open .as-reveal:nth-child(8)  { animation-delay: 450ms; }
.as-search-open .as-reveal:nth-child(9)  { animation-delay: 480ms; }
.as-search-open .as-reveal:nth-child(10) { animation-delay: 510ms; }
.as-search-open .as-reveal:nth-child(n+11) { animation-delay: 540ms; }

.as-search-open .as-footer {
  animation: as-reveal 400ms var(--as-ease-spring) backwards 540ms;
}

@keyframes as-reveal {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ─── Chat shell (chat mode layout) ─────────────────────────────── */
.as-chat-shell {
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  overflow: hidden;
  position: relative;
  animation: as-chat-fade-in 320ms var(--as-ease-out);
}

/* FileDropZone overlay — appears on dragenter when files are
   dragged from the OS over the chat surface. Pill is centered, dashed
   indigo border, frosted background. Pointer-events:none so the drop
   target underneath still receives the drop event. */
.as-drop-host { position: relative; }
.as-drop-overlay {
  pointer-events: none;
  position: absolute;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--as-bg-elevated) 60%, transparent);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}
.as-drop-pill {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: 16px;
  border: 2px dashed var(--ordo-internal-primary-60, hsl(var(--primary) / 0.6));
  background: var(--as-bg-elevated);
  color: var(--as-text);
  font-size: 14px;
  font-weight: 500;
  box-shadow: var(--as-shadow-modal);
}
.as-drop-pill svg { width: 20px; height: 20px; color: var(--ordo-internal-primary, hsl(var(--primary))); }

@keyframes as-chat-fade-in {
  from { opacity: 0; }
  to   { opacity: 1; }
}

/* Header: back-arrow + conversation title on the left, the action
   cluster (new chat / open-in-Agent / close) on the right. Generous
   padding to match the empty-palette's input-row spacing — feels
   like the same surface, not a cramped strip. */
.as-chat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 14px;
  border-bottom: 1px solid var(--as-border);
  flex-shrink: 0;
  animation: as-chat-header-slide 320ms var(--as-ease-out);
}

@keyframes as-chat-header-slide {
  from { transform: translateY(-12px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

/* Conversation title — also the header's flexible middle, so the two
   control slots stay pinned to the edges with no filler element.
   Truncates instead of shoving the action cluster off a narrow
   (mobile, full-screen) palette. */
.as-chat-title {
  flex: 1;
  min-width: 0;
  padding: 0 6px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 500;
  color: var(--as-text-muted);
  letter-spacing: -0.01em;
}

.as-chat-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.as-chat-header-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  /* Floors the labelled New-chat button against its 36px icon-only
     siblings (.as-chat-header-btn-icon sets height, which wins). */
  min-height: 32px;
  padding: 5px 9px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--as-text-muted);
  font: inherit;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  transition: background 100ms, color 100ms;
}
.as-chat-header-btn:hover {
  background: var(--as-hover);
  color: var(--as-text);
}
/* Four controls share this row now and one of them drops the visible
   thread, so a keyboard user has to be able to see which one Enter is
   about to hit. The :disabled pair used to be inherited from
   .as-chat-handoff-link, which went away with the footer. */
.as-chat-header-btn:focus-visible {
  outline: 2px solid var(--as-accent);
  outline-offset: 1px;
  color: var(--as-text);
}
.as-chat-header-btn:disabled { opacity: 0.4; cursor: default; }
.as-chat-header-btn:disabled:hover { background: transparent; color: var(--as-text-muted); }
.as-chat-header-btn svg { width: 13px; height: 13px; }

/* Icon-only header buttons (back-arrow, close). Bigger touch target
   + more visual weight to match the Sparkle-icon-with-padding feel
   of the empty-palette's input row. */
.as-chat-header-btn-icon {
  width: 36px;
  height: 36px;
  padding: 0;
  justify-content: center;
}
.as-chat-header-btn-icon svg { width: 18px; height: 18px; }

.as-chat-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* Composer at bottom — multi-line textarea + paperclip + voice + send */
.as-chat-composer {
  flex-shrink: 0;
  padding: 8px 12px 12px;
  /* Safe area for iOS home indicator */
  padding-bottom: max(12px, env(safe-area-inset-bottom));
  border-top: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
  animation: as-chat-composer-slide 320ms var(--as-ease-spring) backwards 100ms;
}

@keyframes as-chat-composer-slide {
  from { transform: translateY(24px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.as-chat-composer-shell {
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  border-radius: 12px;
  transition: border-color 100ms;
}
.as-chat-composer-shell:focus-within {
  border-color: var(--as-border-strong);
}

.as-chat-textarea-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  /* Horizontal padding lives on the textarea ('!px-2.5' at the call
     site) so the typed text lines up with the action row's left
     edge. Vertical padding only here. */
  padding: 10px 0 4px;
}

.as-chat-composer-sparkle {
  margin-top: 4px;
  flex-shrink: 0;
}

/* The action row (mode picker / paperclip / mic / send) used to be
   hand-rolled here under .as-chat-actions* + .as-chat-action-btn +
   .as-chat-send-btn*. It is now ComposerActionRow from
   @ordo/ui/composer-primitives — the same primitive apps/agent uses
   — so the styling lives in one place and both surfaces stay visually
   in lockstep. Don't add new chat-action selectors here; add them to
   the primitive instead.
   See packages/agent-embed/src/agent-search/chat-shell.tsx. */

/* Generating… status bar — appears above the composer while
   streaming. Mirrors the agent app's layout (separate row with
   spinner + label on the left, white-outlined Stop on the right). */
.as-chat-queue {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 14px;
  margin: 0 4px 6px;
  font-size: 12.5px;
  color: var(--as-text-muted);
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  border-radius: 10px;
}
.as-chat-queue-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.as-chat-queue-clear {
  padding: 2px 8px;
  font: inherit;
  font-size: 12px;
  font-weight: 500;
  color: var(--as-text);
  background: transparent;
  border: 1px solid var(--as-border);
  border-radius: 6px;
  cursor: pointer;
}
.as-chat-queue-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.as-chat-queue-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: var(--as-text);
}
.as-chat-generating {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  margin: 0 4px 6px;
  font-size: 12.5px;
  color: var(--as-text-muted);
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  border-radius: 10px;
}
.as-chat-generating-spin {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.as-chat-generating-spin svg {
  width: 14px;
  height: 14px;
  animation: as-spin 1s linear infinite;
}
@keyframes as-spin {
  to { transform: rotate(360deg); }
}
.as-chat-generating-label {
  flex: 1;
}
/* Attachment chips above textarea */
.as-chat-uploads {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 0 4px 8px;
}
.as-chat-upload-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
  font-size: 12px;
  color: var(--as-text);
  max-width: 200px;
}
.as-chat-upload-chip-error {
  border-color: var(--ordo-internal-destructive-50, hsl(var(--destructive) / 0.5));
  background: var(--ordo-internal-destructive-6, hsl(var(--destructive) / 0.06));
  animation: as-chat-upload-shake 320ms;
}
.as-chat-upload-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 140px;
}
.as-chat-upload-spin {
  width: 12px !important;
  height: 12px !important;
  animation: as-chat-spin 1s linear infinite;
}
/* Shared by the attachment chips and the queued-message rows. 24×24 is
   the WCAG 2.5.8 (AA) floor; it was 16×16, which matters more now that
   this ✕ is the only way to drop a lone queued message. flex-shrink
   stops the flex:1 queue text from squeezing it. */
.as-chat-upload-x {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  color: var(--as-text-muted);
  cursor: pointer;
  border-radius: 3px;
}
.as-chat-upload-x:hover { background: var(--as-hover); }
.as-chat-upload-x svg { width: 12px; height: 12px; }

@keyframes as-chat-spin {
  to { transform: rotate(360deg); }
}
@keyframes as-chat-upload-shake {
  0%, 100% { transform: translateX(0); }
  25%      { transform: translateX(-2px); }
  75%      { transform: translateX(2px); }
}

/* ─── Slash autocomplete dropdown ────────────────────────────────── */
.as-slash-dropdown {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-bottom: 8px;
  padding: 4px;
  border-radius: 10px;
  background: var(--as-bg-elevated);
  border: 1px solid var(--as-border);
  box-shadow: var(--as-shadow-modal);
  max-height: 240px;
  overflow-y: auto;
  animation: as-slash-slide-in 200ms var(--as-ease-spring);
}

@keyframes as-slash-slide-in {
  from { transform: translateY(8px); opacity: 0; }
  to   { transform: translateY(0); opacity: 1; }
}

.as-slash-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  border: none;
  background: transparent;
  font: inherit;
  font-size: 13px;
  color: var(--as-text);
  text-align: left;
  cursor: pointer;
  transition: background 100ms;
}
.as-slash-item-active { background: var(--as-hover); }

.as-slash-item-icon {
  width: 14px;
  height: 14px;
  color: var(--as-text-muted);
  flex-shrink: 0;
}

.as-slash-item-trigger {
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
  font-size: 12px;
  color: var(--as-accent);
  flex-shrink: 0;
}

.as-slash-item-label {
  flex: 1;
  color: var(--as-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.as-slash-item-args {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--as-text-subtle);
  background: var(--as-hover);
  padding: 2px 6px;
  border-radius: 4px;
  flex-shrink: 0;
}

.as-ask-cta {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  margin: 4px 12px 8px;
  border-radius: 10px;
  cursor: pointer;
  background: var(--ordo-internal-primary-6, hsl(var(--primary) / 0.06));
  border: 1px solid var(--ordo-internal-primary-20, hsl(var(--primary) / 0.2));
  color: var(--as-text);
  font-size: 13.5px;
  transition: background 100ms;
}
.as-ask-cta:hover { background: var(--ordo-internal-primary-10, hsl(var(--primary) / 0.10)); }
.as-ask-cta-label { color: var(--as-text-subtle); margin-right: 4px; }
.as-ask-cta-query { font-weight: 500; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Resume-chat row at the top of the empty body when the user toggled
   "back to search" with an active chat. Click → return to chat shell. */
.as-resume-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  margin: 8px 12px;
  border-radius: 10px;
  border: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
  font: inherit;
  color: var(--as-text);
  text-align: left;
  cursor: pointer;
  width: calc(100% - 24px);
  transition: background 100ms, border-color 100ms;
}
.as-resume-row:hover {
  background: var(--as-hover);
  border-color: var(--as-border-strong);
}
/* When the resume row is the active flatItem (default highlight on
   palette open with an active chat), tint the whole card border
   indigo instead of slapping the standard left-edge accent bar on
   it — the row is already a chip-card and the bar reads as "stuck
   to the side of the box" which is visually noisy. */
.as-resume-row.as-row-active {
  background: var(--ordo-internal-primary-6, hsl(var(--primary) / 0.06));
  border-color: var(--ordo-internal-primary-50, hsl(var(--primary) / 0.5));
}
.as-resume-row.as-row-active::before {
  display: none;
}
.as-resume-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--ordo-internal-primary-10, hsl(var(--primary) / 0.1));
  color: var(--ordo-internal-primary, hsl(var(--primary)));
  flex-shrink: 0;
}
.as-resume-icon svg { width: 14px; height: 14px; }
.as-resume-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}
.as-resume-title {
  font-size: 13px;
  font-weight: 500;
  color: var(--as-text);
}
.as-resume-preview {
  font-size: 12px;
  color: var(--as-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.as-resume-prefix {
  /* Inherits everything from .as-resume-preview — no styling overrides.
     Plain text reads as a soft "from whom" cue without competing visual weight. */
}

/* ─── Mobile adaptation ─────────────────────────────────────────────
   On mobile the morph fills the viewport. The closed pill stretches
   the topbar slot full-width; the open state takes nearly the whole
   screen. The X button replaces the "esc" hint as a touch target. */
@media (max-width: 640px) {
  :root {
    /* Full-screen on mobile. dvh (dynamic viewport height) shrinks
       with Safari's URL bar so the morph always covers the visible
       area — using vh would leave a hard unblurred strip at the
       bottom when the URL bar is visible. */
    --as-anchor-top: 0px;
    --as-open-w: 100vw;
    /* var(--as-vh) is set by useVisualViewportHeight() to the true
       visible viewport height — accounts for iOS keyboard + form-
       helper-bar that 100dvh under-reports. Falls back to 100dvh on
       browsers without VisualViewport API. */
    --as-open-h: var(--as-vh, 100dvh);
    /* Don't constrain the closed slot — let it flex between left + right
       AppTopBar siblings (hamburger / notification bell). */
    --as-closed-w: 9999px;
  }
  /* Mobile open state: same animation pattern as desktop — top, left,
     width, height all interpolate smoothly. transform stays
     translateX(-50%) (inherited from .as-search base rule), so it
     never toggles mid-animation. The pill grows from the slot to
     full-screen as one continuous spring.
     top reads --as-vv-top (visualViewport.offsetTop) so when iOS
     Safari scrolls the layout viewport up to bring a focused input
     above the keyboard, the morph stays anchored to the visible area
     instead of sliding off the top. Falls back to 0 when
     visualViewport is unsupported. */
  .as-search-open {
    top: var(--as-vv-top, 0) !important;
    left: 50% !important;
    width: 100vw !important;
    height: var(--as-vh, 100dvh) !important;
    max-width: 100vw;
    max-height: var(--as-vh, 100dvh) !important;
    border-radius: 0 !important;
  }
  /* Hide keyboard hints on touch devices — they're irrelevant. The X
     button in the input row covers the close affordance, so the entire
     footer goes away on mobile. */
  .as-kbd-esc,
  .as-search .as-closed .as-kbd,
  .as-footer {
    display: none;
  }
  /* Result rows match the unified sidebar's phone rows (16px text, 20px
     icon, 12px vertical padding) so they are as easy to tap. */
  .as-row {
    padding: 12px 20px;
  }
  .as-row-icon,
  .as-row-icon > svg {
    width: 20px;
    height: 20px;
  }
  .as-row-title {
    font-size: 16px;
  }
  .as-row-meta {
    font-size: 13px;
  }
}

/* X close button — visible always (mobile + desktop) so mouse users
   have a click-target alongside the esc hint. Sizing matches
   .as-chat-header-btn-icon (36×36 button, 18×18 icon) so the empty-
   palette close and the chat-header close read as the same control —
   they're mentally the same affordance ("dismiss this surface"). */
.as-close-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--as-text-muted);
  cursor: pointer;
  transition: background 100ms;
  flex-shrink: 0;
}
.as-close-btn:hover {
  background: var(--as-hover);
  color: var(--as-text);
}
.as-close-btn svg {
  width: 18px;
  height: 18px;
}

/* Opened from the phone search circle (unified shell): grow out of the
   circle with no overshoot — a springy overshoot on the edges reads as a
   slide, not a grow — and let the circle be the close control. */
.as-search.as-search-from-trigger {
  transition:
    width 460ms var(--as-ease-grow),
    height 460ms var(--as-ease-grow),
    top 460ms var(--as-ease-grow),
    left 460ms var(--as-ease-grow),
    border-radius 460ms var(--as-ease-grow),
    box-shadow 320ms var(--as-ease-out),
    border-color 200ms;
}
/* Opened from the compact icon button (desktop): the same overshoot-free
   grow out of the icon, its closed layer is just the centered icon, and the
   open content waits until the box is nearly full size so the input row
   does not reflow (and wrap) inside a still-small box. */
.as-search.as-search-from-compact {
  transition:
    width 420ms var(--as-ease-grow),
    height 420ms var(--as-ease-grow),
    top 420ms var(--as-ease-grow),
    left 420ms var(--as-ease-grow),
    border-radius 420ms var(--as-ease-grow),
    box-shadow 320ms var(--as-ease-out),
    border-color 200ms;
}
.as-search-from-compact .as-closed {
  justify-content: center;
  padding: 0;
  color: var(--as-text-muted);
}
.as-search-from-compact.as-search-open .as-open {
  transition: opacity 200ms var(--as-ease-out) 240ms;
}
.as-search-from-trigger .as-close-btn {
  display: none;
}
.as-trigger-close {
  position: fixed;
  z-index: 2147483647;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  border: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
  color: var(--as-text);
  cursor: pointer;
  transition: top 300ms var(--as-ease-out), transform 150ms;
}
.as-trigger-close:active {
  transform: scale(0.95);
}
.as-trigger-close svg {
  position: absolute;
  width: 22px;
  height: 22px;
  transition:
    transform 320ms cubic-bezier(0.3, 1.4, 0.5, 1),
    opacity 200ms;
}
.as-trigger-close-x {
  opacity: 0;
  transform: rotate(-90deg) scale(0.6);
}
.as-trigger-close-on .as-trigger-close-search {
  opacity: 0;
  transform: rotate(90deg) scale(0.6);
}
.as-trigger-close-on .as-trigger-close-x {
  opacity: 1;
  transform: none;
}

@media (prefers-reduced-motion: reduce) {
  .as-trigger-close svg {
    transition-duration: 80ms !important;
  }
  .as-search,
  .as-backdrop,
  .as-closed,
  .as-open,
  .as-chip,
  .as-search-open .as-reveal,
  .as-search-open .as-input-row,
  .as-search-open .as-footer,
  .as-chat-shell,
  .as-chat-header,
  .as-chat-composer {
    transition-duration: 80ms !important;
    animation: none !important;
  }
  .as-sparkle-stop-1,
  .as-sparkle-stop-2 { animation: none !important; }
}

/* ============================================================================
   Conversation content — chat bubbles, slash pills, attachments, prose,
   approve-all bar, resolved pills, and the agent-label sparkle tint.

   These were Tailwind utility chains in message-turn.tsx that got purged on
   every host except Tasker (Tasker is the only globals.css with @source for
   agent-embed). Migrated to raw CSS so the embed self-themes on every host
   and zero host configuration is required.

   Naming: .as-msg-* for in-chat content (parallels existing .as-* morph
   chrome). Layout-only utilities (flex, gap, padding) stay as Tailwind —
   they're standard enough to emit on every host's bundle.
   ============================================================================ */

.as-msg-user-bubble {
  min-width: 0;
  word-break: break-word;
  white-space: pre-wrap;
  border-radius: 1rem;
  background: hsl(239 84% 60%);                  /* indigo-600 */
  padding: 0.5rem 0.875rem;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: white;
}
.as-msg-user-bubble::selection {
  background: white;
  color: hsl(244 75% 35%);                       /* indigo-900 */
}

.as-msg-slash-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 9999px;
  border: 1px solid hsl(229 84% 80% / 0.6);      /* indigo-300 / 60% */
  background: hsl(226 100% 97%);                 /* indigo-50 */
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: hsl(229 76% 35%);                       /* indigo-700 */
}
:root.dark .as-msg-slash-pill, .dark .as-msg-slash-pill {
  border-color: hsl(239 84% 67% / 0.4);          /* indigo-500 / 40% */
  background: hsl(239 84% 67% / 0.15);           /* indigo-500 / 15% */
  color: hsl(231 92% 84%);                       /* indigo-200 */
}

.as-msg-attachment-thumb {
  overflow: hidden;
  border-radius: 0.375rem;
  border: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
}
.as-msg-attachment-thumb:hover {
  border-color: var(--as-border-strong);
}

.as-msg-attachment-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: 0.375rem;
  border: 1px solid var(--as-border);
  background: var(--as-bg-elevated);
  padding: 0.25rem 0.5rem;
  color: var(--as-text);
  text-decoration: none;
}
.as-msg-attachment-chip:hover {
  background: var(--as-hover);
}

.as-msg-approve-all {
  margin-top: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  border-radius: 0.5rem;
  border: 1px solid var(--as-border);
  background: color-mix(in srgb, var(--as-hover) 40%, transparent);
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
}
.as-msg-approve-all-btn {
  border-radius: 0.375rem;
  border: none;
  background: var(--as-text);
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--as-bg-elevated);
  cursor: pointer;
  transition: opacity 100ms;
}
.as-msg-approve-all-btn:hover {
  opacity: 0.9;
}

.as-msg-resolved-pill {
  margin-top: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid hsl(160 84% 39% / 0.3);      /* emerald-500 / 30% */
  background: var(--as-bg-elevated);
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  color: var(--as-text-muted);
}
.as-msg-resolved-pill svg {
  color: hsl(158 64% 40%);                       /* emerald-600 */
}

.as-msg-sparkle {
  color: hsl(239 84% 67%);                       /* indigo-500 */
}
:root.dark .as-msg-sparkle, .dark .as-msg-sparkle {
  color: hsl(234 89% 74%);                       /* indigo-400 */
}

/* Starter-card icon tile — small grid square with tinted indigo bg + icon. */
.as-starter-icon {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  flex-shrink: 0;
  border-radius: 0.375rem;
  background: hsl(239 84% 67% / 0.1);
  color: hsl(239 84% 67%);
  transition: background 100ms;
}
.as-starter-icon:hover,
.as-starter-row:hover .as-starter-icon,
.group:hover .as-starter-icon {
  background: hsl(239 84% 67% / 0.15);
}

/* Send button on the unified composer — indigo pill with hover bump. */
.as-send-button {
  background: hsl(239 84% 60%);                  /* indigo-600 */
  color: white;
  transition: background 100ms;
}
.as-send-button:hover:not(:disabled) {
  background: hsl(239 84% 67%);                  /* indigo-500 */
}

/* Voice recording indicator — red dot with concentric ping ring. */
.as-rec-dot {
  position: relative;
  display: inline-flex;
  width: 0.625rem;
  height: 0.625rem;
  border-radius: 9999px;
  background: hsl(0 84% 60%);                    /* red-500 */
}
.as-rec-pulse {
  position: absolute;
  inset: 0;
  display: inline-flex;
  border-radius: 9999px;
  background: hsl(0 84% 60% / 0.6);              /* red-500 / 60% */
  animation: as-ping 1s cubic-bezier(0, 0, 0.2, 1) infinite;
}
@keyframes as-ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

/* Markdown / prose styling. Replaces the long prose-* Tailwind chain that
   gets applied to dangerouslySetInnerHTML output (which Tailwind can't
   scan even with @source). Covers the elements renderMarkdown emits:
   p, h1-h3, ul, ol, li, code, pre, a, blockquote, hr, table, img. */
.as-msg-prose {
  min-width: 0;
  max-width: none;
  color: var(--as-text);
  font-size: 0.9375rem;
  line-height: 1.625;
}
.as-msg-prose > * + * { margin-top: 0.5rem; }
.as-msg-prose p { margin: 0.5rem 0; }
.as-msg-prose h1, .as-msg-prose h2, .as-msg-prose h3 {
  font-weight: 600;
  margin: 1rem 0 0.5rem;
  line-height: 1.3;
}
.as-msg-prose h1 { font-size: 1.25rem; }
.as-msg-prose h2 { font-size: 1.125rem; }
.as-msg-prose h3 { font-size: 1rem; }
.as-msg-prose a {
  color: var(--as-accent);
  text-decoration: underline;
  text-underline-offset: 2px;
  word-break: break-word;
}
.as-msg-prose ul, .as-msg-prose ol {
  padding-left: 1.5rem;
  margin: 0.5rem 0;
}
.as-msg-prose ul { list-style: disc; }
.as-msg-prose ol { list-style: decimal; }
.as-msg-prose li { margin: 0.25rem 0; }
.as-msg-prose li > p { margin: 0; }
.as-msg-prose code {
  border-radius: 0.25rem;
  background: var(--as-hover);
  padding: 0.125rem 0.25rem;
  font-size: 0.85em;
  font-family: ui-monospace, "SF Mono", Menlo, monospace;
}
.as-msg-prose pre {
  border-radius: 0.375rem;
  background: var(--as-hover);
  padding: 0.75rem;
  margin: 0.75rem 0;
  overflow-x: auto;
  font-size: 0.85em;
}
.as-msg-prose pre code {
  background: transparent;
  padding: 0;
  font-size: inherit;
}
.as-msg-prose blockquote {
  border-left: 3px solid var(--as-border);
  padding-left: 0.75rem;
  margin: 0.5rem 0;
  color: var(--as-text-muted);
}
.as-msg-prose hr {
  border: none;
  border-top: 1px solid var(--as-border);
  margin: 1rem 0;
}
.as-msg-prose table {
  border-collapse: collapse;
  margin: 0.75rem 0;
  font-size: 0.9em;
}
.as-msg-prose th, .as-msg-prose td {
  border: 1px solid var(--as-border);
  padding: 0.375rem 0.625rem;
  text-align: left;
}
.as-msg-prose th {
  background: var(--as-hover);
  font-weight: 600;
}
.as-msg-prose img {
  max-width: 100%;
  border-radius: 0.375rem;
  margin: 0.75rem 0;
}
`

/** Inject the morph stylesheet into <head>. Updates the existing tag's
 *  content on every call so Vite HMR picks up changes (the previous
 *  early-return on an existing tag pinned the first-load CSS forever). */
export function ensureAgentSearchStyles(): void {
  if (typeof document === 'undefined') {
    return
  }
  let style = document.getElementById(AGENT_SEARCH_STYLE_ID) as HTMLStyleElement | null
  if (!style) {
    style = document.createElement('style')
    style.id = AGENT_SEARCH_STYLE_ID
    document.head.appendChild(style)
  }
  if (style.textContent !== agentSearchCss) {
    style.textContent = agentSearchCss
  }
}
